/**
 * LT display formatting (spec-web-ui UX bar) — pure functions, unit-tested
 * in test/ui/format.test.ts. Kept DOM-free: this module compiles under both
 * the ui and the root tsconfig (see ui/tsconfig.json note).
 */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * `YYYY-MM-DD HH:mm` in the VIEWER'S local time (the wire carries UTC ISO
 * strings; employees think in Vilnius wall-clock). Unparseable/absent
 * input renders as the honest empty cell, never "Invalid Date".
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    ` ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  );
}

/**
 * RELATIVE age in LT ("prieš 5 min."), for the check-out lock badge
 * (spec-artifact-locking §8: "taken by someone — holder, SINCE"). A lock
 * has no TTL, so its AGE is the whole staleness signal a colleague gets —
 * an absolute timestamp would make every reader do the subtraction.
 *
 * `now` is a parameter, never `Date.now()` inside: the caller owns the
 * clock, so this stays pure and the texts are pinned deterministically in
 * test/ui/format.test.ts. Unparseable/absent input and clock skew (a lock
 * stamped in the FUTURE — server/browser clocks differ) both collapse to
 * "ką tik", never a negative age or "Invalid Date".
 */
export function formatAgo(iso: string | null | undefined, now: number): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "ką tik";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `prieš ${minutes} min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `prieš ${hours} val.`;
  return `prieš ${Math.floor(hours / 24)} d.`;
}

/**
 * Human byte size, mirroring the ModulinaPanele3 dialog's buckets
 * (B / KB rounded / MB one decimal) + a GB bucket for the 500 MB-cap era.
 */
export function formatSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
